import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { csvFilename } from '../helper/csvExport';
import useIsMobile from '../hooks/useIsMobile';
import { MOBILE_TOUCH_MIN_PX, mobileDialogPaperSx } from '../theme/mobileLayout';

function countCopy(review) {
  if (review?.countKind === 'known' && review.expectedRowCount !== null) {
    return `${review.expectedRowCount.toLocaleString()} rows`;
  }
  if (review?.countKind === 'estimated' && review.expectedRowCount !== null) {
    return `About ${review.expectedRowCount.toLocaleString()} rows (estimate)`;
  }
  return 'Count unavailable';
}

export default function ExportReviewDialog({
  open,
  review,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}) {
  const [filename, setFilename] = useState('');
  const confirmGuard = useRef(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!open) return;
    setFilename(review?.suggestedFilename || '');
    confirmGuard.current = false;
  }, [open, review]);

  useEffect(() => {
    if (!busy) confirmGuard.current = false;
  }, [busy]);

  const handleConfirm = () => {
    if (busy || confirmGuard.current) return;
    confirmGuard.current = true;
    onConfirm(csvFilename(filename, review?.suggestedFilename));
  };

  const warnings = review?.warnings || [];

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      fullWidth
      fullScreen={isMobile}
      maxWidth="sm"
      PaperProps={{
        sx: {
          ...mobileDialogPaperSx(isMobile, { maxWidth: 560 }),
          maxHeight: isMobile ? '100dvh' : 'calc(100dvh - 32px)',
        },
      }}
    >
      <DialogTitle>Review export</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography><strong>Source:</strong> {review?.sourceLabel}</Typography>
            <Typography><strong>Scope:</strong> {review?.scopeLabel}</Typography>
            <Typography>
              <strong>Companies:</strong>{' '}
              {review?.companyCount === null ? 'Count unavailable' : review?.companyCount.toLocaleString()}
            </Typography>
            <Typography><strong>Expected CSV rows:</strong> {countCopy(review)}</Typography>
            <Typography variant="caption" color="text.secondary">
              Count kind: {review?.countKind || 'unavailable'}
            </Typography>
          </Stack>

          {warnings.map((warning, index) => (
            <Alert severity="warning" key={warning.code || `${warning.message}-${index}`}>
              {warning.message}
            </Alert>
          ))}

          {error && <Alert severity="error">{error}</Alert>}

          <TextField
            autoFocus
            fullWidth
            label="Filename"
            value={filename}
            onChange={(event) => setFilename(event.target.value)}
            helperText={`Downloads as ${csvFilename(filename, review?.suggestedFilename)}`}
            disabled={busy}
            inputProps={{ 'aria-label': 'Filename' }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          px: 2,
          pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', sm: 2 },
          '& > :not(style)': { m: 0 },
        }}
      >
        <Button onClick={onCancel} disabled={busy} sx={{ minHeight: MOBILE_TOUCH_MIN_PX }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={busy}
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ minHeight: MOBILE_TOUCH_MIN_PX }}
        >
          {busy ? 'Exporting…' : 'Download CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
